/**
 * @fileoverview Walkthrough Hook
 * @description Primary hook for consuming walkthrough state and triggering walkthroughs.
 * Provides a clean API for components to interact with the walkthrough engine.
 * @module features/walkthrough/hooks/useWalkthrough
 */

import { useCallback } from 'react';
import { useWalkthroughContext } from '../context/WalkthroughContext';
import type { WalkthroughDefinition, WalkthroughProgress } from '../types';

interface UseWalkthroughReturn {
  /** Currently active walkthrough definition */
  activeWalkthrough: WalkthroughDefinition | null;
  /** Progress for the active walkthrough */
  activeProgress: WalkthroughProgress | null;
  /** Current step in the active walkthrough */
  currentStep: WalkthroughDefinition['steps'][number] | null;
  /** Current step index (0-based) */
  currentStepIndex: number;
  /** Total number of steps in the active walkthrough */
  totalSteps: number;
  /** Whether a walkthrough is currently active */
  isActive: boolean;
  /** Whether the engine is loading */
  isLoading: boolean;
  /** Start a walkthrough by its ID */
  startWalkthrough: (id: string) => void;
  /** Advance to the next step */
  nextStep: () => void;
  /** Go back to the previous step */
  prevStep: () => void;
  /** Skip the current step */
  skipStep: () => void;
  /** Complete a specific step */
  completeStep: (stepId: string) => void;
  /** Dismiss the active walkthrough */
  dismiss: () => void;
  /** Complete the active walkthrough */
  complete: () => void;
  /** Reset a walkthrough's progress */
  reset: (id: string) => void;
  /** Check if a specific walkthrough has been completed */
  isCompleted: (id: string) => boolean;
  /** Check if a specific walkthrough has been started */
  isStarted: (id: string) => boolean;
  /** Get progress for a specific walkthrough */
  getProgress: (id: string) => WalkthroughProgress | null;
  /**
   * Start a walkthrough only if it hasn't been completed yet.
   * Returns true if the walkthrough was started, false if already completed.
   */
  startIfNotCompleted: (id: string) => boolean;
}

export function useWalkthrough(): UseWalkthroughReturn {
  const ctx = useWalkthroughContext();

  const currentStepIndex = ctx.activeProgress?.currentStepIndex ?? 0;
  const totalSteps = ctx.activeWalkthrough?.steps.length ?? 0;
  const currentStep = ctx.activeWalkthrough?.steps[currentStepIndex] ?? null;

  const startIfNotCompleted = useCallback(
    (id: string): boolean => {
      if (ctx.isWalkthroughCompleted(id)) {
        return false;
      }
      // Already showing — skip. Page effects re-fire when context identity
      // changes after nextStep; re-calling start must not wipe the step index.
      if (ctx.activeWalkthrough?.id === id) {
        return false;
      }
      ctx.startWalkthrough(id);
      return true;
    },
    [ctx]
  );

  return {
    activeWalkthrough: ctx.activeWalkthrough,
    activeProgress: ctx.activeProgress,
    currentStep,
    currentStepIndex,
    totalSteps,
    isActive: ctx.activeWalkthrough !== null,
    isLoading: ctx.isLoading,
    startWalkthrough: ctx.startWalkthrough,
    nextStep: ctx.nextStep,
    prevStep: ctx.prevStep,
    skipStep: ctx.skipStep,
    completeStep: ctx.completeStep,
    dismiss: ctx.dismissWalkthrough,
    complete: ctx.completeWalkthrough,
    reset: ctx.resetWalkthrough,
    isCompleted: ctx.isWalkthroughCompleted,
    isStarted: ctx.isWalkthroughStarted,
    getProgress: ctx.getProgress,
    startIfNotCompleted,
  };
}
